import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { SalesLayout } from '../components/SalesLayout';
import { useNavigate, useLocation } from 'react-router-dom';
import { Pagination } from '../components/Pagination';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getDefaultDateRange = () => {
  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const formatDate = (d) => d.toISOString().split('T')[0];
  return { from: formatDate(firstOfMonth), to: formatDate(now) };
};

// Indian currency formatter
const formatIndianCurrency = (amount) => {
  if (!amount && amount !== 0) return '-';
  const num = parseFloat(amount);
  if (isNaN(num)) return '-';
  return '₹' + num.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN');
  } catch {
    return dateStr;
  }
};

// Chart colors
const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f97316', '#10b981', '#06b6d4', '#f59e0b', '#84cc16'];

// Loan status badge
const getLoanStatusBadge = (status) => {
  const statusLower = status?.toLowerCase() || 'pending';
  const styles = {
    'pending': 'bg-yellow-100 text-yellow-800 border-yellow-300',
    'approved': 'bg-blue-100 text-blue-800 border-blue-300',
    'disbursed': 'bg-green-100 text-green-800 border-green-300',
    'rejected': 'bg-red-100 text-red-800 border-red-300',
  };
  return styles[statusLower] || 'bg-gray-100 text-gray-800 border-gray-300';
};

function FinancierReport({ user, onLogout }) {
  const navigate = useNavigate();
  const [bookings, setBookings] = React.useState([]);
  const [filteredData, setFilteredData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [masters, setMasters] = React.useState({ financiers: [] });
  const defaultRange = getDefaultDateRange();
  const [dateFrom, setDateFrom] = React.useState(defaultRange.from);
  const [dateTo, setDateTo] = React.useState(defaultRange.to);
  const [selectedFinancier, setSelectedFinancier] = React.useState('');
  const [selectedLoanStatus, setSelectedLoanStatus] = React.useState('');
  const [searchValue, setSearchValue] = React.useState('');
  const [activeView, setActiveView] = React.useState('table'); // 'table', 'charts'
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = React.useState(false);
  const [editingBooking, setEditingBooking] = React.useState(null);
  const [editData, setEditData] = React.useState({});
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    fetchData();
  }, []);

  React.useEffect(() => {
    applyFilter();
  }, [bookings, dateFrom, dateTo, selectedFinancier, selectedLoanStatus, searchValue]);

  const fetchData = async () => {
    try {
      const [bookingsRes, mastersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/bookings?limit=10000`),
        axios.get(`${BACKEND_URL}/api/masters?type=financier`)
      ]);
      
      // Filter only financed bookings (using financiers_name field from BookingForm)
      const financed = bookingsRes.data.filter(b => 
        b.financiers_name && b.financiers_name.trim() !== ''
      );
      setBookings(financed);
      setMasters({ financiers: mastersRes.data });
    } catch (error) {
      toast.error('Failed to fetch data');
    }
    setLoading(false);
  };

  const applyFilter = () => {
    let filtered = [...bookings];
    
    // Date filter
    if (dateFrom) {
      filtered = filtered.filter(b => {
        const bookingDate = b.date_of_booking || b.dms_invoice_date;
        return bookingDate && bookingDate >= dateFrom;
      });
    }
    if (dateTo) {
      filtered = filtered.filter(b => {
        const bookingDate = b.date_of_booking || b.dms_invoice_date;
        return bookingDate && bookingDate <= dateTo;
      });
    }
    
    // Financier filter
    if (selectedFinancier) {
      filtered = filtered.filter(b => b.financiers_name === selectedFinancier);
    }
    
    // Loan status filter
    if (selectedLoanStatus) {
      filtered = filtered.filter(b => b.loan_status?.toLowerCase() === selectedLoanStatus.toLowerCase());
    }
    
    // Search filter
    if (searchValue.trim()) {
      const search = searchValue.toLowerCase();
      filtered = filtered.filter(b =>
        b.booking_no?.toString().includes(search) ||
        b.customer_first_name?.toLowerCase().includes(search) ||
        b.customer_last_name?.toLowerCase().includes(search) ||
        b.mobile_no_1?.includes(search) ||
        b.loan_account_no?.toLowerCase().includes(search)
      );
    }
    
    setFilteredData(filtered);
  };

  // Get unique financiers from data
  const uniqueFinanciers = React.useMemo(() => {
    const financiers = [...new Set(bookings.map(b => b.financiers_name).filter(Boolean))];
    return financiers.sort();
  }, [bookings]);

  // Group by Financier with detailed metrics
  const groupedData = React.useMemo(() => {
    const groups = {};
    filteredData.forEach(booking => {
      const financier = booking.financiers_name || 'Unknown';
      if (!groups[financier]) {
        groups[financier] = {
          items: [],
          totalDisbursement: 0,
          totalEMI: 0,
          avgRate: 0,
          avgTenure: 0,
          statusCounts: { pending: 0, approved: 0, disbursed: 0, rejected: 0 }
        };
      }
      groups[financier].items.push(booking);
      groups[financier].totalDisbursement += parseFloat(booking.disbursement_amount) || 0;
      groups[financier].totalEMI += parseFloat(booking.emi_amount) || 0;
      
      const status = booking.loan_status?.toLowerCase() || 'pending';
      if (groups[financier].statusCounts[status] !== undefined) {
        groups[financier].statusCounts[status]++;
      }
    });
    
    // Calculate averages
    Object.keys(groups).forEach(key => {
      const g = groups[key];
      const validRates = g.items.filter(b => b.rate_of_interest > 0);
      const validTenures = g.items.filter(b => b.tenure_months > 0);
      g.avgRate = validRates.length > 0 
        ? (validRates.reduce((sum, b) => sum + parseFloat(b.rate_of_interest || 0), 0) / validRates.length).toFixed(2)
        : 0;
      g.avgTenure = validTenures.length > 0
        ? Math.round(validTenures.reduce((sum, b) => sum + parseInt(b.tenure_months || 0), 0) / validTenures.length)
        : 0;
    });
    
    return groups;
  }, [filteredData]);

  // Chart data
  const chartData = React.useMemo(() => {
    return Object.entries(groupedData).map(([name, data]) => ({
      name: name.length > 15 ? name.substring(0, 15) + '...' : name,
      fullName: name,
      disbursement: data.totalDisbursement,
      count: data.items.length,
      avgRate: parseFloat(data.avgRate)
    }));
  }, [groupedData]);

  // Summary metrics
  const summary = React.useMemo(() => {
    const totalDisbursement = Object.values(groupedData).reduce((sum, d) => sum + d.totalDisbursement, 0);
    const totalEMI = Object.values(groupedData).reduce((sum, d) => sum + d.totalEMI, 0);
    const statusCounts = { pending: 0, approved: 0, disbursed: 0, rejected: 0 };
    Object.values(groupedData).forEach(d => {
      statusCounts.pending += d.statusCounts.pending;
      statusCounts.approved += d.statusCounts.approved;
      statusCounts.disbursed += d.statusCounts.disbursed;
      statusCounts.rejected += d.statusCounts.rejected;
    });
    return { totalDisbursement, totalEMI, statusCounts };
  }, [groupedData]);

  // Edit booking
  const handleEdit = (booking) => {
    setEditingBooking(booking);
    setEditData({
      financiers_name: booking.financiers_name || '',
      date_of_disbursement: booking.date_of_disbursement || '',
      disbursement_amount: booking.disbursement_amount || '',
      loan_status: booking.loan_status || '',
      loan_account_no: booking.loan_account_no || '',
      emi_amount: booking.emi_amount || '',
      tenure_months: booking.tenure_months || '',
      rate_of_interest: booking.rate_of_interest || ''
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingBooking) return;
    
    try {
      setSaving(true);
      await axios.put(`${BACKEND_URL}/api/bookings/${editingBooking.id}`, {
        ...editingBooking,
        ...editData
      });
      toast.success('Finance details updated successfully');
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to update: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  // Print report
  const handlePrint = () => {
    window.print();
  };

  // Export to Excel via backend
  const handleExportExcel = async () => {
    try {
      toast.info('Generating Excel...');
      const bookingIds = filteredData.map(b => b.id);
      const response = await axios.post(`${BACKEND_URL}/api/bookings/export-excel-custom`, {
        booking_ids: bookingIds,
        fields: ['booking_no', 'customer_name', 'mobile_no_1', 'vehicle', 'financiers_name', 
                 'date_of_disbursement', 'disbursement_amount', 'loan_status', 'loan_account_no',
                 'emi_amount', 'tenure_months', 'rate_of_interest']
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Financier_Report_${dateFrom}_to_${dateTo}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel downloaded');
    } catch (error) {
      toast.error('Failed to export Excel');
    }
  };

  if (loading) {
    return (
      <SalesLayout user={user} onLogout={onLogout} title="Financier Report">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </SalesLayout>
    );
  }

  return (
    <SalesLayout user={user} onLogout={onLogout} title="Financier Report">
      <div className="space-y-4 print:space-y-2">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-5 text-white print:hidden">
          <h1 className="text-2xl font-bold">💰 Financier-wise Report</h1>
          <p className="opacity-90 text-sm mt-1">Manage and track all financed bookings with detailed metrics</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm border print:hidden">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">From Date</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full p-2 border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">To Date</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full p-2 border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Financier</label>
              <select
                value={selectedFinancier}
                onChange={(e) => setSelectedFinancier(e.target.value)}
                className="w-full p-2 border rounded-lg text-sm"
              >
                <option value="">All Financiers</option>
                {uniqueFinanciers.map(f => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Loan Status</label>
              <select
                value={selectedLoanStatus}
                onChange={(e) => setSelectedLoanStatus(e.target.value)}
                className="w-full p-2 border rounded-lg text-sm"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="disbursed">Disbursed</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
              <input
                type="text"
                placeholder="Name, Mobile, Loan A/C..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="w-full p-2 border rounded-lg text-sm"
              />
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={handleExportExcel}
                className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
              >
                📊 Excel
              </button>
              <button
                onClick={handlePrint}
                className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
              >
                🖨️ Print
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 print:grid-cols-6">
          <div className="bg-indigo-50 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-indigo-600">{filteredData.length}</div>
            <div className="text-xs text-indigo-700">Total Bookings</div>
          </div>
          <div className="bg-purple-50 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-purple-600">{Object.keys(groupedData).length}</div>
            <div className="text-xs text-purple-700">Financiers</div>
          </div>
          <div className="bg-green-50 rounded-xl p-3 text-center">
            <div className="text-lg font-bold text-green-600">{formatIndianCurrency(summary.totalDisbursement)}</div>
            <div className="text-xs text-green-700">Total Disbursement</div>
          </div>
          <div className="bg-blue-50 rounded-xl p-3 text-center">
            <div className="text-lg font-bold text-blue-600">{formatIndianCurrency(summary.totalEMI)}</div>
            <div className="text-xs text-blue-700">Monthly EMI</div>
          </div>
          <div className="bg-yellow-50 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-yellow-600">{summary.statusCounts.pending}</div>
            <div className="text-xs text-yellow-700">Pending</div>
          </div>
          <div className="bg-emerald-50 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-emerald-600">{summary.statusCounts.disbursed}</div>
            <div className="text-xs text-emerald-700">Disbursed</div>
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex gap-2 print:hidden">
          <button
            onClick={() => setActiveView('table')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              activeView === 'table' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            📋 Table View
          </button>
          <button
            onClick={() => setActiveView('charts')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              activeView === 'charts' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            📊 Charts View
          </button>
        </div>

        {/* Charts View */}
        {activeView === 'charts' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 print:hidden">
            {/* Disbursement by Financier */}
            <div className="bg-white rounded-xl p-4 border shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4">Disbursement by Financier</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" fontSize={10} angle={-45} textAnchor="end" height={80} />
                  <YAxis tickFormatter={(v) => `₹${(v/100000).toFixed(0)}L`} />
                  <Tooltip formatter={(v) => formatIndianCurrency(v)} />
                  <Bar dataKey="disbursement" fill="#6366f1" name="Disbursement" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Booking Count by Financier */}
            <div className="bg-white rounded-xl p-4 border shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4">Booking Count by Financier</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={100}
                    dataKey="count"
                  >
                    {chartData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Table View */}
        {activeView === 'table' && (
          <div className="space-y-4" id="report-content">
            {Object.keys(groupedData).length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center text-gray-500 border">
                No financed bookings found for the selected criteria
              </div>
            ) : (
              Object.entries(groupedData).map(([financier, data]) => (
                <div key={financier} className="bg-white rounded-xl border shadow-sm overflow-hidden">
                  {/* Financier Header */}
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-4 py-3">
                    <div className="flex justify-between items-center flex-wrap gap-2">
                      <div>
                        <h3 className="font-bold text-lg">{financier}</h3>
                        <p className="text-sm opacity-90">{data.items.length} bookings</p>
                      </div>
                      <div className="flex gap-4 text-sm">
                        <div>
                          <span className="opacity-75">Disbursement:</span>
                          <span className="font-bold ml-1">{formatIndianCurrency(data.totalDisbursement)}</span>
                        </div>
                        <div>
                          <span className="opacity-75">Avg Rate:</span>
                          <span className="font-bold ml-1">{data.avgRate}%</span>
                        </div>
                        <div>
                          <span className="opacity-75">Avg Tenure:</span>
                          <span className="font-bold ml-1">{data.avgTenure} months</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-indigo-50">
                        <tr>
                          <th className="p-3 text-left font-semibold text-gray-700">#</th>
                          <th className="p-3 text-left font-semibold text-gray-700">B.No</th>
                          <th className="p-3 text-left font-semibold text-gray-700">Customer</th>
                          <th className="p-3 text-left font-semibold text-gray-700">Mobile</th>
                          <th className="p-3 text-left font-semibold text-gray-700">Vehicle</th>
                          <th className="p-3 text-left font-semibold text-gray-700">Disbursement Date</th>
                          <th className="p-3 text-left font-semibold text-gray-700">Amount</th>
                          <th className="p-3 text-left font-semibold text-gray-700">Loan Status</th>
                          <th className="p-3 text-left font-semibold text-gray-700">Loan A/C No.</th>
                          <th className="p-3 text-left font-semibold text-gray-700">EMI</th>
                          <th className="p-3 text-left font-semibold text-gray-700">Tenure</th>
                          <th className="p-3 text-left font-semibold text-gray-700">Rate</th>
                          <th className="p-3 text-left font-semibold text-gray-700 print:hidden">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.items.map((b, idx) => (
                          <tr key={b.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-indigo-50/30'}>
                            <td className="p-3">{idx + 1}</td>
                            <td className="p-3 font-medium text-indigo-700">{b.booking_no}</td>
                            <td className="p-3">{b.customer_first_name} {b.customer_last_name || ''}</td>
                            <td className="p-3">{b.mobile_no_1 || '-'}</td>
                            <td className="p-3">{b.model} {b.variant}</td>
                            <td className="p-3">{formatDate(b.date_of_disbursement)}</td>
                            <td className="p-3 font-medium">{formatIndianCurrency(b.disbursement_amount)}</td>
                            <td className="p-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getLoanStatusBadge(b.loan_status)}`}>
                                {b.loan_status || 'pending'}
                              </span>
                            </td>
                            <td className="p-3">{b.loan_account_no || '-'}</td>
                            <td className="p-3">{formatIndianCurrency(b.emi_amount)}</td>
                            <td className="p-3">{b.tenure_months ? `${b.tenure_months} mo` : '-'}</td>
                            <td className="p-3">{b.rate_of_interest ? `${b.rate_of_interest}%` : '-'}</td>
                            <td className="p-3 print:hidden">
                              <button
                                onClick={() => handleEdit(b)}
                                className="px-2 py-1 text-blue-600 hover:bg-blue-100 rounded text-xs font-medium"
                              >
                                Edit
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && editingBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 print:hidden">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold mb-2 text-gray-800">
              Edit Finance Details - Booking #{editingBooking.booking_no}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {editingBooking.customer_first_name} {editingBooking.customer_last_name}
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Financier Name</label>
                <select
                  value={editData.financiers_name}
                  onChange={(e) => setEditData({ ...editData, financiers_name: e.target.value })}
                  className="w-full p-2 border rounded-lg"
                >
                  <option value="">Select Financier</option>
                  {masters.financiers.map(f => (
                    <option key={f.id} value={f.name}>{f.name}</option>
                  ))}
                  {/* Also include current value if not in masters */}
                  {editData.financiers_name && !masters.financiers.find(f => f.name === editData.financiers_name) && (
                    <option value={editData.financiers_name}>{editData.financiers_name}</option>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date of Disbursement</label>
                <input
                  type="date"
                  value={editData.date_of_disbursement}
                  onChange={(e) => setEditData({ ...editData, date_of_disbursement: e.target.value })}
                  className="w-full p-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Disbursement Amount (₹)</label>
                <input
                  type="number"
                  value={editData.disbursement_amount}
                  onChange={(e) => setEditData({ ...editData, disbursement_amount: e.target.value })}
                  className="w-full p-2 border rounded-lg"
                  placeholder="Enter amount"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Loan Status</label>
                <select
                  value={editData.loan_status}
                  onChange={(e) => setEditData({ ...editData, loan_status: e.target.value })}
                  className="w-full p-2 border rounded-lg"
                >
                  <option value="">Select Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="disbursed">Disbursed</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Loan Account No.</label>
                <input
                  type="text"
                  value={editData.loan_account_no}
                  onChange={(e) => setEditData({ ...editData, loan_account_no: e.target.value })}
                  className="w-full p-2 border rounded-lg"
                  placeholder="Enter loan account number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">EMI Amount (₹)</label>
                <input
                  type="number"
                  value={editData.emi_amount}
                  onChange={(e) => setEditData({ ...editData, emi_amount: e.target.value })}
                  className="w-full p-2 border rounded-lg"
                  placeholder="Monthly EMI amount"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tenure (Months)</label>
                  <input
                    type="number"
                    value={editData.tenure_months}
                    onChange={(e) => setEditData({ ...editData, tenure_months: e.target.value })}
                    className="w-full p-2 border rounded-lg"
                    placeholder="e.g., 36"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rate of Interest (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editData.rate_of_interest}
                    onChange={(e) => setEditData({ ...editData, rate_of_interest: e.target.value })}
                    className="w-full p-2 border rounded-lg"
                    placeholder="e.g., 8.5"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                disabled={saving}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-100 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium disabled:bg-indigo-400"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </SalesLayout>
  );
}

export default FinancierReport;
